/**
 * Single source of truth for invoice amounts on the frontend.
 * Mirrors backend/invoice_calc.py - keep both in sync.
 *
 *   subtotal     = sum(qty * unit_price)
 *   DPP          = subtotal - potongan_retur                -> basis for PPn & PPh
 *   totalTagihan = DPP + PPn + biaya_angkut + pembulatan - dp_amount  -> "Total Tagihan"
 *   pphAmount    = DPP * pph_percentage%
 *   nilaiDibayarkan = totalTagihan - pphAmount - adminBank  -> stored as total_amount
 *
 * Pembulatan corrects the bill, not the tax base: it is an arbitrary rounding
 * figure, so letting it move the DPP would move PPn and PPh with it and the
 * voucher would no longer agree with the Faktur Pajak. It is applied at total
 * level, next to biaya angkut, and may be negative.
 */

const num = (value) => Number(value) || 0;

/** Statuses whose money already left the company - their amounts are frozen. */
export const SETTLED_STATUSES = ['bank_process', 'terbayar', 'sebagian_terbayar', 'paid'];

/** True when an invoice's amounts must be read as stored, never recomputed. */
export function isSettled(invoice) {
  if (!invoice) return false;
  return SETTLED_STATUSES.includes(invoice.status) || Boolean(invoice.paid_at);
}

export function computeAmounts({
  subtotal,
  pembulatan = 0,
  potonganRetur = 0,
  hasPpn = false,
  ppnPercentage = 0,
  biayaAngkut = 0,
  dpAmount = 0,
  pphType = null,
  pphPercentage = 0,
  adminBank = 0,
}) {
  const dpp = num(subtotal) - num(potonganRetur);
  const ppnAmount = hasPpn ? dpp * (num(ppnPercentage) / 100) : 0;
  const pphAmount = pphType && num(pphPercentage) > 0 ? dpp * (num(pphPercentage) / 100) : 0;
  const totalTagihan = dpp + ppnAmount + num(biayaAngkut) + num(pembulatan) - num(dpAmount);

  return {
    frozen: false,
    subtotal: num(subtotal),
    pembulatan: num(pembulatan),
    potonganRetur: num(potonganRetur),
    dpp,
    hasPpn,
    ppnPercentage: num(ppnPercentage),
    ppnAmount,
    biayaAngkut: num(biayaAngkut),
    dpAmount: num(dpAmount),
    totalTagihan,
    pphType: pphType || null,
    pphPercentage: pphType ? num(pphPercentage) : 0,
    pphAmount,
    adminBank: num(adminBank),
    nilaiDibayarkan: totalTagihan - pphAmount - num(adminBank),
  };
}

/** Sum of an invoice's item lines. */
export function sumItems(items) {
  return (items || []).reduce((total, item) => total + num(item.quantity) * num(item.unit_price), 0);
}

/**
 * Amounts exactly as they were stored, for invoices that have already been paid.
 * Reading them back instead of recomputing is what keeps a formula change from
 * rewriting the figures on a voucher that is already printed and filed.
 */
function storedAmounts(invoice) {
  const pembulatan = num(invoice.pembulatan);
  const ppnAmount = num(invoice.ppn_amount);
  const biayaAngkut = num(invoice.biaya_angkut);
  const dpAmount = num(invoice.dp_amount);
  const pphAmount = num(invoice.pph_amount);
  const adminBank = num(invoice.admin_bank);
  const totalAmount = num(invoice.total_amount);

  // `dpp` and `total_bruto` were added later; documents older than that carry
  // neither, so fall back to inverting the formula those documents were written
  // with - where pembulatan still sat inside the DPP.
  const totalTagihan =
    invoice.total_bruto != null ? num(invoice.total_bruto) : totalAmount + pphAmount + adminBank;
  const dpp =
    invoice.dpp != null ? num(invoice.dpp) : totalTagihan - ppnAmount - biayaAngkut + dpAmount;

  return {
    frozen: true,
    subtotal: invoice.subtotal != null ? num(invoice.subtotal) : sumItems(invoice.items),
    pembulatan,
    potonganRetur: num(invoice.potongan_retur),
    dpp,
    hasPpn: Boolean(invoice.has_ppn),
    ppnPercentage: num(invoice.ppn_percentage),
    ppnAmount,
    biayaAngkut,
    dpAmount,
    totalTagihan,
    pphType: invoice.pph_type || null,
    pphPercentage: invoice.pph_type ? num(invoice.pph_percentage) : 0,
    pphAmount,
    adminBank,
    nilaiDibayarkan: totalAmount,
  };
}

/**
 * Amounts of a stored invoice, recomputed from its raw fields so every screen
 * shows the same numbers even when a document predates a calculation fix.
 *
 * Settled invoices are the exception: they report what was stored, so the money
 * already disbursed is never restated. Passing `overrides` (an edit dialog
 * previewing unsaved input) always recomputes - a settled invoice is not
 * editable in the first place.
 *
 * `overrides` accepts the same keys as computeAmounts.
 */
export function invoiceAmounts(invoice, overrides = {}) {
  if (!invoice) return computeAmounts({ subtotal: 0, ...overrides });
  const hasOverrides = Object.keys(overrides || {}).length > 0;
  if (!hasOverrides && isSettled(invoice)) return storedAmounts(invoice);
  return computeAmounts({
    subtotal: invoice.subtotal != null ? num(invoice.subtotal) : sumItems(invoice.items),
    pembulatan: invoice.pembulatan,
    potonganRetur: invoice.potongan_retur,
    hasPpn: invoice.has_ppn,
    ppnPercentage: invoice.ppn_percentage,
    biayaAngkut: invoice.biaya_angkut,
    dpAmount: invoice.dp_amount,
    pphType: invoice.pph_type,
    pphPercentage: invoice.pph_percentage,
    adminBank: invoice.admin_bank,
    ...overrides,
  });
}
